import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { trainingBlockApi, trainingProgramApi } from '../../api'
import { trainingProgramActivationApi } from '../../api/trainingProgramActivationApi'
import { userApi } from '../../api/userApi'
import { workoutApi } from '../../api/workoutApi'
import type { WorkoutSession } from '../../api/workoutApi'
import type { TrainingTagDto } from '../../api/trainingBlockApi'
import { flattenTrainingTags } from '../../api/trainingBlockApi'
import type { ApiError } from '../../api/client'
import type { TrainingBlock, TrainingProgram, TrainingProgramActivation, UserRelation } from '../../types/api'
import { activationMatchesTemplateProgram } from '../../types/api'
import { Button } from '../ui'
import { TrainingBlockCard } from './TrainingBlockCard'
import ActiveWorkoutWidget from './ActiveWorkoutWidget'
import { useTranslation } from '../../i18n/useTranslation'
import { useTheme } from '../../hooks/useTheme'
import { useAuth } from '../../contexts/AuthContext'
import { entitlementsOf, isCoachedTrainee } from '../../utils/entitlements'
import ActivateProgramSheet from './ActivateProgramSheet'
import SoloLibraryLocked from './SoloLibraryLocked'
import AssignToTraineeSheet from './AssignToTraineeSheet'
import { WORKOUT_FINISHED_EVENT } from '../../utils/workoutEvents'
import { captureEvent } from '../../posthog'

export default function TrainingProgramDetail() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const theme = useTheme()
  const { t } = useTranslation()
  const { user } = useAuth()
  const isCoach = user?.hasTrainees ?? false
  const canActivateOwn = entitlementsOf(user).createOwnBlocksAndPrograms
  const canUseWorkoutMode = entitlementsOf(user).workoutMode

  const [program, setProgram] = useState<TrainingProgram | null>(null)
  const [blocks, setBlocks] = useState<TrainingBlock[]>([])
  const [availableTags, setAvailableTags] = useState<TrainingTagDto[]>([])
  const [activation, setActivation] = useState<TrainingProgramActivation | null>(null)
  const [trainees, setTrainees] = useState<UserRelation[]>([])
  const [assignedActivations, setAssignedActivations] = useState<Map<number, TrainingProgramActivation>>(new Map())
  const [showActivateSheet, setShowActivateSheet] = useState(false)
  const [showAssignSheet, setShowAssignSheet] = useState(false)
  const [deactivating, setDeactivating] = useState(false)
  const [savingProgram, setSavingProgram] = useState(false)
  const [savingBlockId, setSavingBlockId] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [startingBlockId, setStartingBlockId] = useState<number | null>(null)
  const [workoutError, setWorkoutError] = useState<string | null>(null)
  const [activeSession, setActiveSession] = useState<WorkoutSession | null>(null)

  const blockMap = useMemo(
    () => Object.fromEntries(blocks.map(b => [b.id, b])),
    [blocks]
  )

  const tagEmojiMap = useMemo(
    () => Object.fromEntries(availableTags.map(tag => [tag.key, tag.emoji])),
    [availableTags]
  )

  const tagLabelMap = useMemo(
    () => Object.fromEntries(availableTags.map(tag => [tag.key, tag.label])),
    [availableTags]
  )

  useEffect(() => {
    if (!id) return
    const programId = Number(id)
    const base = Promise.all([
      trainingProgramApi.getProgram(programId),
      trainingProgramActivationApi.getHistory(),
      trainingBlockApi.getTags(),
    ]).then(async ([programData, history, tagsData]) => {
      const currentActivation = history.find(
        a => a.isActive && activationMatchesTemplateProgram(a, programId)
      ) ?? null
      const isAssignedProgram = currentActivation?.assignedByCoachId != null
      setActivation(currentActivation)
      setAvailableTags(flattenTrainingTags(tagsData))
      setProgram(programData)
      if (!isAssignedProgram) {
        const blocksData = await trainingBlockApi.getBlocks()
        setBlocks(blocksData)
      }
    })

    const coachData = isCoach
      ? userApi.getTrainees().then(async (traineeList) => {
          setTrainees(traineeList)
          const active = traineeList.filter(tr => tr.relationStatus === 'ACTIVE')
          const results = await Promise.all(
            active.map(tr =>
              trainingProgramActivationApi.getTraineeActiveAssignment(tr.id)
                .then(a => ({ traineeId: tr.id, activation: a }))
                .catch(() => ({ traineeId: tr.id, activation: null }))
            )
          )
          const map = new Map<number, TrainingProgramActivation>()
          for (const { traineeId, activation: a } of results) {
            if (a && a.isActive && activationMatchesTemplateProgram(a, programId)) {
              map.set(traineeId, a)
            }
          }
          setAssignedActivations(map)
        })
      : Promise.resolve()

    Promise.all([base, coachData])
      .catch((err: ApiError) => setError(err.message || t.trainingPrograms.error.loadFailed))
      .finally(() => setLoading(false))
  }, [id, t, isCoach])

  useEffect(() => {
    workoutApi.getActiveSession()
      .then(session => setActiveSession(session))
      .catch(() => setActiveSession(null))
    const refresh = () => {
      workoutApi.getActiveSession()
        .then(session => setActiveSession(session))
        .catch(() => setActiveSession(null))
    }
    window.addEventListener(WORKOUT_FINISHED_EVENT, refresh)
    return () => window.removeEventListener(WORKOUT_FINISHED_EVENT, refresh)
  }, [])

  async function handleDelete() {
    if (!program) return
    if (!confirm(t.trainingPrograms.error.deleteConfirm.replace('{{name}}', program.name))) return
    try {
      await trainingProgramApi.deleteProgram(program.id)
      navigate('/training-programs')
    } catch (err) {
      const apiError = err as ApiError
      alert(apiError.message || t.trainingPrograms.error.deleteFailed)
    }
  }

  async function handleDeactivate() {
    if (!program || !activation || activation.assignedByCoachId != null) return
    setDeactivating(true)
    try {
      await trainingProgramActivationApi.deactivate(activation.programId)
      setActivation(null)
    } catch (err) {
      const apiError = err as ApiError
      alert(apiError.message || t.trainingPrograms.activate.error.deactivateFailed)
    } finally {
      setDeactivating(false)
    }
  }

  async function handleSaveProgram() {
    if (!program) return
    setSavingProgram(true)
    try {
      const updated = await trainingProgramApi.saveProgram(program.id)
      setProgram(updated)
    } catch (err) {
      const apiError = err as ApiError
      alert(apiError.message || t.trainingPrograms.error.loadFailed)
    } finally {
      setSavingProgram(false)
    }
  }

  async function startBlock(blockId: number) {
    if (activeSession?.id != null) {
      navigate(`/training/session/${activeSession.id}`, { replace: true })
      return
    }
    if (!canUseWorkoutMode) {
      navigate('/subscription')
      return
    }
    setStartingBlockId(blockId)
    setWorkoutError(null)
    try {
      const session = await workoutApi.startSession(blockId, activation?.id ?? null)
      captureEvent('workout_started', {
        session_id: session.id,
        block_id: blockId,
        program_id: activation?.programId,
        source: 'program_detail',
      })
      navigate(`/training/session/${session.id}`, { replace: true })
    } catch (err) {
      const apiError = err as ApiError
      if (apiError.status === 403 || apiError.message?.includes('subscription.workout.mode.required')) {
        navigate('/subscription')
      } else {
        setWorkoutError(apiError.message || t.workout.error.startFailed)
      }
    } finally {
      setStartingBlockId(null)
    }
  }

  async function handleSaveBlock(blockId: number) {
    setSavingBlockId(blockId)
    try {
      await trainingBlockApi.saveBlock(blockId)
      setProgram(prev => {
        if (!prev) return prev
        return {
          ...prev,
          days: prev.days.map(day =>
            day.blockId === blockId ? { ...day, blockIsSaved: true } : day
          ),
        }
      })
    } catch (err) {
      const apiError = err as ApiError
      alert(apiError.message || t.trainingPrograms.error.loadFailed)
    } finally {
      setSavingBlockId(null)
    }
  }

  const isAssigned = activation?.assignedByCoachId != null
  const isOwner = program != null && user?.id != null && program.userId === user.id
  const readOnly = searchParams.get('readonly') === '1'
  const canMutate = !isAssigned && !readOnly
  const assignedTraineeIds = Array.from(assignedActivations.keys())

  if (loading) {
    return <div className="p-5 text-center opacity-60 pt-16">{t.trainingPrograms.loading}</div>
  }

  if (error || !program) {
    return (
      <div className="p-5">
        <div className="rounded-xl p-4 bg-red-500/10 border border-red-500/25">
          <p className="text-sm text-red-500">{error || t.trainingPrograms.error.loadFailed}</p>
        </div>
      </div>
    )
  }

  if (isCoachedTrainee(user) && !isAssigned) {
    return <SoloLibraryLocked pageTitle={t.trainingPrograms.title} />
  }

  return (
    <div className="p-5 pb-24">
      {/* Header */}
      <header className="mb-6">
        <div className="flex items-start justify-between gap-3">
          <h1 className="text-2xl font-bold leading-tight" style={{ color: theme.text.primary }}>
            {program.name}
          </h1>
          {canMutate && (
            <div className="flex items-center gap-1 shrink-0 mt-0.5">
              <button
                type="button"
                onClick={() => navigate(`/training-programs/${program.id}/edit`)}
                className="p-2.5 rounded-xl"
                style={{ color: theme.accent.text }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className="p-2.5 rounded-xl"
                style={{ color: '#ef4444' }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                  <path d="M10 11v6"/><path d="M14 11v6"/>
                  <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                </svg>
              </button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 mt-1">
          <p className="text-sm" style={{ color: theme.text.muted }}>
            {t.trainingPrograms.blockCount_other.replace('{{count}}', String(program.days.length))}
          </p>
        </div>
        {isOwner && program.isSaved === false && (
          <button
            type="button"
            disabled={savingProgram}
            onClick={handleSaveProgram}
            className="mt-3 text-xs font-semibold px-3 py-2 rounded-xl"
            style={{
              color: theme.accent.text,
              backgroundColor: `${theme.buttonColor}12`,
              border: `1px solid ${theme.buttonColor}25`,
            }}
          >
            {savingProgram ? t.trainingPrograms.savingToLibrary : t.trainingPrograms.saveToLibrary}
          </button>
        )}
        {program.tags && program.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {program.tags.map(tag => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full"
                style={{
                  backgroundColor: `${theme.buttonColor}12`,
                  border: `1px solid ${theme.buttonColor}25`,
                  color: theme.accent.text,
                }}
              >
                <span className="leading-none">{tagEmojiMap[tag] ?? ''}</span>
                <span>
                  {t.trainingTags[tag as keyof typeof t.trainingTags] ?? tagLabelMap[tag] ?? tag}
                </span>
              </span>
            ))}
          </div>
        )}
      </header>

      {workoutError && (
        <div
          className="rounded-xl p-3 mb-4 text-sm"
          style={{ backgroundColor: 'rgba(239,68,68,0.12)', color: theme.text.primary }}
        >
          {workoutError}
        </div>
      )}

      {activeSession?.id != null && (
        <ActiveWorkoutWidget workout={activeSession} />
      )}

      {program.days.length === 0 ? (
        <p className="text-sm text-center py-8" style={{ color: theme.text.muted }}>
          {t.trainingPrograms.emptyState.description}
        </p>
      ) : (
        <div className="mb-2">
          {activation && (
            <h2 className="text-sm font-bold mb-3" style={{ color: theme.text.primary }}>
              {t.workout.programBlocks}
            </h2>
          )}
          <div className="flex flex-col gap-3">
          {program.days.map(day => {
            const block = blockMap[day.blockId]
            const dayItems = day.items ?? block?.items ?? []
            const tags = day.blockTags ?? block?.tags ?? []
            const canOpenBlock = canMutate
            const canStartWorkout = !readOnly && canUseWorkoutMode && activation != null && activeSession?.id == null
            const showFooter = canOpenBlock || canStartWorkout

            return (
              <TrainingBlockCard
                key={day.id}
                name={day.blockName ?? `Block #${day.blockId}`}
                items={dayItems}
                tags={tags}
                tagEmojiMap={tagEmojiMap}
                tagLabelMap={tagLabelMap}
                index={day.sortOrder + 1}
                onClick={
                  canStartWorkout
                    ? () => startBlock(day.blockId)
                    : canOpenBlock
                      ? () => navigate(`/training-blocks/${day.blockId}/edit`)
                      : undefined
                }
                footer={showFooter ? (
                  <div className="flex items-center justify-between gap-1 w-full min-w-0">
                    <div className="flex items-center gap-1 min-w-0">
                      {canOpenBlock && (
                        <>
                          <button
                            type="button"
                            title={t.trainingBlocks.editBlock}
                            onClick={() => navigate(`/training-blocks/${day.blockId}/edit`)}
                            className="p-3 rounded-xl transition-colors"
                            style={{ color: theme.accent.text }}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                            </svg>
                          </button>
                          {isOwner && day.blockIsSaved === false && (
                            <button
                              type="button"
                              disabled={savingBlockId === day.blockId}
                              onClick={() => handleSaveBlock(day.blockId)}
                              className="px-3 py-3 rounded-xl text-xs font-semibold transition-colors disabled:opacity-40"
                              style={{ color: theme.accent.text }}
                            >
                              {savingBlockId === day.blockId
                                ? t.trainingPrograms.savingToLibrary
                                : t.trainingPrograms.saveBlockToLibrary}
                            </button>
                          )}
                        </>
                      )}
                    </div>
                    {canStartWorkout && (
                      <Button
                        type="button"
                        variant="brand"
                        size="sm"
                        disabled={startingBlockId === day.blockId}
                        onClick={() => startBlock(day.blockId)}
                      >
                        {startingBlockId === day.blockId ? t.workout.loading : t.workout.startBlock}
                      </Button>
                    )}
                  </div>
                ) : undefined}
              />
            )
          })}
          </div>
        </div>
      )}

      {canMutate && (
        <div className="mt-6 flex flex-col gap-3">
          {isCoach ? (
            <>
              {activation ? (
                <Button
                  type="button"
                  variant="secondary"
                  size="lg"
                  fullWidth
                  onClick={handleDeactivate}
                  disabled={deactivating}
                >
                  {t.trainingPrograms.deactivate}
                </Button>
              ) : (
                canActivateOwn && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="lg"
                    fullWidth
                    onClick={() => setShowActivateSheet(true)}
                  >
                    {t.trainingPrograms.activate.forMyselfButton}
                  </Button>
                )
              )}
              <Button
                type="button"
                variant="primary"
                size="lg"
                fullWidth
                onClick={() => setShowAssignSheet(true)}
              >
                {t.trainingPrograms.assignProgram.assignButton}
              </Button>
            </>
          ) : (
            <>
              {activation ? (
                <Button
                  type="button"
                  variant="secondary"
                  size="lg"
                  fullWidth
                  onClick={handleDeactivate}
                  disabled={deactivating}
                >
                  {t.trainingPrograms.deactivate}
                </Button>
              ) : (
                canActivateOwn && (
                  <Button
                    type="button"
                    variant="primary"
                    size="lg"
                    fullWidth
                    onClick={() => setShowActivateSheet(true)}
                  >
                    {t.trainingPrograms.activate.title}
                  </Button>
                )
              )}
            </>
          )}
          <Button
            type="button"
            variant="secondary"
            size="lg"
            fullWidth
            onClick={() => navigate(`/training-programs/${program.id}/edit`)}
          >
            {t.trainingPrograms.editProgram}
          </Button>
        </div>
      )}

      {showActivateSheet && (
        <ActivateProgramSheet
          programId={program.id}
          programName={program.name}
          onClose={() => setShowActivateSheet(false)}
          onActivated={newActivation => {
            setActivation(newActivation)
            setShowActivateSheet(false)
          }}
        />
      )}

      {showAssignSheet && (
        <AssignToTraineeSheet
          programId={program.id}
          programName={program.name}
          trainees={trainees}
          assignedTraineeIds={assignedTraineeIds}
          onClose={() => setShowAssignSheet(false)}
          onAssigned={(newActivation, traineeId) => {
            setAssignedActivations(prev => new Map(prev).set(traineeId, newActivation))
            setShowAssignSheet(false)
          }}
        />
      )}
    </div>
  )
}
